import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { ClipboardList, Package, FileText, Clock, Trash2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/sonner";
import { ProcurementHistoryDialog } from "@/components/products/ProcurementHistoryDialog";
import BuyerItemFields from "@/components/procurement/BuyerItemFields";

const IMG_BASE = process.env.REACT_APP_BACKEND_URL || "";

/**
 * 采购报价要求 link page (2026-06). Opened by the 采购 from the link the 外销
 * generated. Everything here is READ-ONLY; the 采购 files prices through the
 * standard 采购报价 form, pre-filled with the quote number as 报价客户.
 */
export default function ProcurementRequest() {
  const { token } = useParams();
  const { t } = useLanguage();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [history, setHistory] = useState(null); // { itemNumber, before } | null

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/procurement-links/${token}`);
      setData(data);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Link error");
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  if (error) {
    return (
      <div className="p-10 text-center text-destructive" data-testid="procurement-link-error">{error}</div>
    );
  }
  if (!data) {
    return <div className="p-10 text-center text-muted-foreground" data-testid="procurement-link-loading">…</div>;
  }

  const deleteRow = async (item, row) => {
    try {
      await api.delete(`/procurement-links/${token}/items/${encodeURIComponent(item.item_number)}/rows/${row.id}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const openQuoteForm = async (item) => {    const key = item.buyer_item_number || item.item_number;
    let before = 0;
    try {
      const { data } = await api.get("/procurement-quotes", { params: { item_number: key, page_size: 1 } });
      before = data.total || 0;
    } catch { /* count is best-effort — used only for the 已填报 marker */ }
    setHistory({ itemNumber: key, before });
  };

  // When the 采购 closes the records dialog, flag the quote item if they added
  // any new 采购报价 rows for this product.
  const closeHistory = async () => {
    const ctx = history;
    setHistory(null);
    if (!ctx) return;
    try {
      const { data } = await api.get("/procurement-quotes", { params: { item_number: ctx.itemNumber, page_size: 1 } });
      const added = (data.total || 0) - ctx.before;
      if (added > 0) {
        await api.post(`/procurement-links/${token}/mark`, { item_number: ctx.itemNumber, added });
        toast.success(t("procReq.submitted"));
        load();
      }
    } catch { /* marker is best-effort */ }
  };

  return (
    <div className="animate-fade-in-up max-w-5xl mx-auto" data-testid="procurement-request-page">
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <h1 className="font-heading text-base font-bold tracking-tight flex items-center gap-2">
          <ClipboardList size={22} /> {t("procReq.title")}
        </h1>
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-border bg-secondary/40 font-mono text-xs"
          data-testid="procurement-request-quote-number">
          <FileText size={12} /> {data.quote_number}
        </span>
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-border bg-secondary/40 text-xs"
          data-testid="procurement-request-buyer">
          {t("products.buyer")}: {data.buyer}
        </span>
        <span className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground ml-auto">
          <Clock size={12} /> {t("procReq.expires").replace("{date}", (data.expires_at || "").slice(0, 10))}
        </span>
      </div>

      {data.items.length === 0 && (
        <div className="bg-card border border-border rounded-md p-10 text-center text-muted-foreground"
          data-testid="procurement-request-empty">
          {t("procReq.empty")}
        </div>
      )}

      <div className="space-y-4">
        {data.items.map((item) => (
          <div key={item.item_number} className="bg-card border border-border rounded-md p-4 flex flex-col md:flex-row gap-4"
            data-testid={`procurement-request-item-${item.item_number}`}>
            <div className="w-full md:w-40 h-40 shrink-0 rounded-md border border-border bg-secondary/20 flex items-center justify-center overflow-hidden">
              {item.photo_url ? (
                <img src={`${IMG_BASE}${item.photo_url}`} alt={item.item_number} className="w-full h-full object-contain" />
              ) : (
                <Package size={28} className="text-muted-foreground" />
              )}
            </div>
            <div className="flex-1 min-w-0 space-y-2">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-mono text-sm font-bold text-brand">{item.buyer_item_number || item.item_number}</p>
                  <p className="text-sm">{item.description || "-"}</p>
                </div>
                <Button size="sm" onClick={() => openQuoteForm(item)}
                  data-testid={`procurement-request-quote-button-${item.item_number}`}
                  className="rounded-md bg-brand hover:bg-brand-hover shrink-0">
                  <ClipboardList size={14} /> {t("procReq.fileQuote")}
                </Button>
              </div>
              {item.specification && (
                <div>
                  <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{t("quote.editor.specMaterial")}</p>
                  <p className="text-xs font-mono whitespace-pre-wrap">{item.specification}</p>
                </div>
              )}
              <BuyerItemFields
                item={item}
                onSave={async (fields) => {
                  try {
                    await api.patch(`/procurement-links/${token}/items/${encodeURIComponent(item.item_number)}`, fields);
                  } catch (e) {
                    toast.error(formatApiErrorDetail(e.response?.data?.detail));
                  }
                }}
              />
              {item.sizes?.length > 0 && (
                <div>
                  <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{t("quote.size")}</p>
                  <p className="text-xs font-mono">{item.sizes.map((s) => s.size).filter(Boolean).join(" · ") || "-"}</p>
                </div>
              )}
              <div className="rounded-md border border-brand/30 bg-brand/5 px-3 py-2">
                <p className="text-[10px] uppercase tracking-wide text-brand font-semibold">{t("quote.editor.procurementNote")}</p>
                <p className="text-xs whitespace-pre-wrap" data-testid={`procurement-request-note-${item.item_number}`}>
                  {item.procurement_note || t("procReq.noNote")}
                </p>
              </div>
              {/* 价格明细栏 — imported straight from 采购报价 records */}
              <div className="space-y-1">
                <p className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold">{t("procReq.priceRows")}</p>
                <div className="overflow-x-auto border border-border rounded-md">
                  <table className="w-full text-xs" data-testid={`procurement-request-rows-${item.item_number}`}>
                    <thead className="bg-secondary/40">
                      <tr>
                        {[t("quote.size"), t("procReq.factoryCost"), t("procReq.qtyPerCtn"),
                          t("procReq.caseSize"), t("procReq.containerQty"), ""].map((h, i) => (
                          <th key={h || `x${i}`} className="px-2 py-1.5 text-left font-semibold text-[10px] uppercase tracking-wide text-muted-foreground whitespace-nowrap">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {(item.price_rows || []).length === 0 && (
                        <tr><td colSpan={6} className="px-2 py-3 text-center text-muted-foreground">{t("procReq.noRows")}</td></tr>
                      )}
                      {(item.price_rows || []).map((r) => (
                        <tr key={r.id} className="border-t border-border/60">
                          <td className="px-2 py-1.5 font-mono whitespace-nowrap">{r.size || "-"}</td>
                          <td className="px-2 py-1.5 font-mono">{r.factory_cost ? `${r.factory_cost} ${r.cost_currency || ""}` : "-"}</td>
                          <td className="px-2 py-1.5 font-mono">{r.qty_per_ctn || "-"}</td>
                          <td className="px-2 py-1.5 font-mono whitespace-nowrap">
                            {r.case_length ? `${r.case_length}×${r.case_width}×${r.case_height}` : "-"}
                          </td>
                          <td className="px-2 py-1.5 font-mono">{r.container_qty_40 || "-"}</td>
                          <td className="px-1 py-1.5 text-right">
                            {r.can_delete && (
                              <button
                                onClick={() => deleteRow(item, r)}
                                className="p-1 rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                                title={t("procReq.deleteRow")}
                                data-testid={`link-row-delete-${r.id}`}
                              >
                                <Trash2 size={13} />
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <ProcurementHistoryDialog
        open={Boolean(history)}
        onOpenChange={(o) => { if (!o) closeHistory(); }}
        buyerItemNumber={history?.itemNumber}
        onImport={async (rows) => {
          if (!rows?.length) return;
          try {
            await api.post(`/procurement-links/${token}/items/${history.itemNumber}/rows`, { rows });
            toast.success(t("procReq.imported").replace("{n}", String(rows.length)));
            setHistory(null);
            load();
          } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail));
          }
        }}
      />

    </div>
  );
}
